import React, { useRef, useEffect } from 'react';

const DynamicTopographicCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let step = 0;
    let mouse = { x: -1000, y: -1000 };

    const handleResize = () => {
      if (canvas.parentElement) {
        canvas.width = canvas.parentElement.clientWidth;
        canvas.height = canvas.parentElement.clientHeight;
      }
    };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };

    const handleMouseLeave = () => {
      mouse.x = -1000;
      mouse.y = -1000;
    };

    const parent = canvas.parentElement;
    window.addEventListener('resize', handleResize);
    if (parent) {
      parent.addEventListener('mousemove', handleMouseMove);
      parent.addEventListener('mouseleave', handleMouseLeave);
    }
    handleResize();

    const animate = () => {
      step += 0.012;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const lines = 8;
      const height = canvas.height || 800;
      const width = canvas.width || 1200;

      for (let i = 0; i < lines; i++) {
        ctx.beginPath();
        ctx.lineWidth = 1.2;
        const baseOffsetY = (i + 1) * (height / (lines + 1));
        const alpha = 0.05 + (i % 2) * 0.03;

        ctx.strokeStyle = i % 2 === 0 ? `rgba(59, 130, 246, ${alpha})` : `rgba(16, 185, 129, ${alpha})`;

        for (let x = 0; x <= width; x += 12) {
          // Interactive ripple offset based on distance to cursor
          const dx = x - mouse.x;
          const dy = baseOffsetY - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const mouseEffect = dist < 200 ? Math.cos(dist * 0.03) * (200 - dist) * 0.18 : 0;

          const waveY = baseOffsetY + Math.sin(x * 0.006 + step + i * 0.8) * 18 + Math.cos(x * 0.009 - step) * 12 - mouseEffect;

          if (x === 0) {
            ctx.moveTo(x, waveY);
          } else {
            ctx.lineTo(x, waveY);
          }
        }
        ctx.stroke();
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (parent) {
        parent.removeEventListener('mousemove', handleMouseMove);
        parent.removeEventListener('mouseleave', handleMouseLeave);
      }
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1
      }}
    />
  );
};

const About = () => {
  const founders = [
    {
      id: 1,
      name: 'Sahil Sanjay Vaidya',
      role: 'CEO & Strategist',
      tagline: 'Bridging business vision with technical execution',
      bio: 'Sahil drives the human-centric vision, market positioning, and strategic growth of AutoMachina. He specializes in aligning high-level enterprise goals with tailored, scalable AI solutions.',
      image: '/founders/ceo.webp',
      accentColor: 'linear-gradient(90deg, #3B82F6, #60A5FA)',
      badgeBg: 'rgba(59, 130, 246, 0.1)',
      badgeColor: '#3B82F6',
      objectPosition: 'center 5%'
    },
    {
      id: 2,
      name: 'Shreemun',
      role: 'CTO & Architect',
      tagline: 'Engineering robust machine learning infrastructure',
      bio: 'Shreemun leads the core technical architecture and Machine Learning engineering at AutoMachina. He focuses on building high-throughput data pipelines, secure cloud infrastructure, and proprietary model automation.',
      image: '/founders/cto.webp',
      accentColor: 'linear-gradient(90deg, #10B981, #34D399)',
      badgeBg: 'rgba(16, 185, 129, 0.1)',
      badgeColor: '#10B981',
      objectPosition: 'center 5%'
    }
  ];

  return (
    <section id="about" style={{
      padding: '9rem 0',
      backgroundColor: '#F8FAFC',
      borderTop: '1px solid rgba(0, 0, 0, 0.06)',
      borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* 1. Unique Dynamic Topographic Wave Canvas */}
      <DynamicTopographicCanvas />

      {/* 2. Soft Ambient Lighting Orbs */}
      <div 
        className="ambient-orb-1"
        style={{
          position: 'absolute',
          top: '10%',
          left: '15%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.06) 0%, transparent 70%)',
          filter: 'blur(50px)',
          pointerEvents: 'none',
          zIndex: 0
        }} 
      />
      <div 
        className="ambient-orb-2"
        style={{
          position: 'absolute',
          bottom: '10%',
          right: '12%',
          width: '550px',
          height: '550px',
          background: 'radial-gradient(circle, rgba(16, 185, 129, 0.05) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
          zIndex: 0
        }} 
      />

      {/* 3. Hairline Accents */}
      <div style={{
        position: 'absolute',
        top: '15%',
        left: 0,
        right: 0,
        height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.12), transparent)',
        pointerEvents: 'none',
        zIndex: 0
      }} />
      <div style={{
        position: 'absolute',
        bottom: '15%',
        left: 0,
        right: 0,
        height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.12), transparent)',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        
        {/* Section Header */}
        <div className="reveal" style={{ marginBottom: '4.5rem', textAlign: 'center' }}>
          <span className="section-label section-label-light">
            Who We Are
          </span>
          <h2 style={{
            fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
            fontWeight: 800,
            marginBottom: '1rem',
            letterSpacing: '-0.03em',
            lineHeight: 1.1,
            color: 'var(--accent-slate)'
          }}>
            The Minds Behind <span style={{ color: '#4E9A06', textTransform: 'uppercase', letterSpacing: '0.02em' }}>AUTOMACHINA.</span>
          </h2>
          <p style={{
            color: 'var(--text-muted)',
            fontSize: '1.2rem',
            fontWeight: 300,
            maxWidth: '620px',
            margin: '0 auto'
          }}>
            Combining strategic leadership with high-performance AI craftsmanship.
          </p>
        </div>

        {/* Founders Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
          gap: '3rem',
          maxWidth: '1040px',
          margin: '0 auto'
        }}>
          {founders.map((founder, index) => (
            <div
              key={founder.id}
              className="reveal"
              style={{ transitionDelay: `${index * 0.2}s` }}
            >
              <div
                className="founder-card"
                style={{
                  background: 'rgba(255, 255, 255, 0.92)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  borderRadius: '28px',
                  padding: '1.5rem',
                  border: '1px solid rgba(0,0,0,0.06)',
                  boxShadow: '0 20px 50px rgba(0,0,0,0.05)',
                  position: 'relative',
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
                }}
              >
                {/* Top Accent Bar */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: founder.accentColor,
                  zIndex: 2
                }} />

                {/* Founder Portrait Photo */}
                <div 
                  className="founder-image-container"
                  style={{
                    width: '100%',
                    height: '380px',
                    borderRadius: '20px',
                    overflow: 'hidden',
                    backgroundColor: '#E2E8F0',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.04)',
                    border: '1px solid rgba(0,0,0,0.04)',
                    position: 'relative'
                  }}
                >
                  <img 
                    src={founder.image} 
                    alt={founder.name}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: founder.objectPosition,
                      display: 'block',
                      transition: 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)'
                    }}
                    className="founder-photo"
                  />

                  {/* Gradient Overlay at Bottom of Photo */}
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '80px',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.3) 0%, transparent 100%)',
                    pointerEvents: 'none'
                  }} />
                </div>

                {/* Founder Info Section */}
                <div style={{
                  padding: '1.75rem 0.75rem 0.75rem',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.35rem 0.85rem',
                      borderRadius: '100px',
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      backgroundColor: founder.badgeBg,
                      color: founder.badgeColor
                    }}>
                      {founder.role}
                    </span>
                  </div>

                  <h3 style={{
                    fontSize: '1.65rem',
                    fontWeight: 800,
                    color: 'var(--accent-slate)',
                    marginBottom: '0.35rem',
                    letterSpacing: '-0.02em'
                  }}>
                    {founder.name}
                  </h3>

                  <p style={{
                    fontSize: '0.88rem',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    marginBottom: '1.25rem',
                    fontStyle: 'italic'
                  }}>
                    "{founder.tagline}"
                  </p>

                  <p style={{
                    color: 'var(--text-main)',
                    fontSize: '0.95rem',
                    lineHeight: 1.7,
                    marginTop: 'auto'
                  }}>
                    {founder.bio}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes floatOrb1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(30px, -20px) scale(1.1); }
        }
        @keyframes floatOrb2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-30px, 20px) scale(1.08); }
        }
        .ambient-orb-1 {
          animation: floatOrb1 14s infinite ease-in-out;
        }
        .ambient-orb-2 {
          animation: floatOrb2 16s infinite ease-in-out;
        }
        .founder-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 30px 70px rgba(0,0,0,0.12) !important;
        }
        .founder-card:hover .founder-photo {
          transform: scale(1.03);
        }
      `}</style>
    </section>
  );
};

export default About;
